import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, ArrowUpCircle, ArrowDownCircle, Pencil, Search, Filter, Calendar } from 'lucide-react';
import { formatCurrency, getCurrencySymbol } from '@/utils/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Predefined categories
const INCOME_CATEGORIES = [
  'Salary',
  'Freelance/Contract',
  'Business Income',
  'Interest',
  'Dividends',
  'Rental Income',
  'Bonus',
  'Gift/Inheritance',
  'Refund',
  'Other Income'
];

const EXPENSE_CATEGORIES = [
  'Rent/EMI',
  'Groceries',
  'Dining/Restaurants',
  'Shopping/Clothing',
  'Transportation/Fuel',
  'Electricity/Utilities',
  'Internet/Mobile',
  'Healthcare/Medical',
  'Insurance',
  'Education',
  'Entertainment',
  'Travel/Vacation',
  'Personal Care',
  'Household',
  'Subscriptions',
  'Gifts/Donations',
  'Investments',
  'Loan Repayment',
  'Taxes',
  'Other Expenses'
];

export default function Transactions({ user, onLogout }) {
  const [transactions, setTransactions] = useState([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('current');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');
  
  const [formData, setFormData] = useState({
    type: 'expense',
    category: '',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data);
    } catch (error) {
      toast.error('Failed to fetch transactions');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        date: new Date(formData.date).toISOString()
      };

      if (editMode) {
        // Update existing transaction
        await axios.put(
          `${API}/transactions/${editingId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Transaction updated successfully!');
      } else {
        // Create new transaction
        await axios.post(
          `${API}/transactions`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Transaction added successfully!');
      }
      
      setOpen(false);
      setEditMode(false);
      setEditingId(null);
      setFormData({
        type: 'expense',
        category: '',
        amount: '',
        description: '',
        date: new Date().toISOString().split('T')[0]
      });
      fetchTransactions();
    } catch (error) {
      toast.error(editMode ? 'Failed to update transaction' : 'Failed to add transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (transaction) => {
    setEditMode(true);
    setEditingId(transaction.id);
    setFormData({
      type: transaction.type,
      category: transaction.category,
      amount: transaction.amount.toString(),
      description: transaction.description,
      date: new Date(transaction.date).toISOString().split('T')[0]
    });
    setOpen(true);
  };

  const handleAddNew = () => {
    setEditMode(false);
    setEditingId(null);
    setFormData({
      type: 'expense',
      category: '',
      amount: '',
      description: '',
      date: new Date().toISOString().split('T')[0]
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/transactions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transaction deleted!');
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to delete transaction');
    }
  };

  // Get unique categories from transactions
  const categories = useMemo(() => {
    const cats = [...new Set(transactions.map(t => t.category))];
    return cats.sort();
  }, [transactions]);

  // Get available months from transactions
  const availableMonths = useMemo(() => {
    const months = {};
    transactions.forEach(t => {
      const date = new Date(t.date);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      if (!months[key]) {
        months[key] = { key, label, count: 0 };
      }
      months[key].count++;
    });
    return Object.values(months).sort((a, b) => b.key.localeCompare(a.key));
  }, [transactions]);

  // Filter and sort transactions
  const filteredTransactions = useMemo(() => {
    let filtered = [...transactions];

    // Filter by month
    if (selectedMonth !== 'all') {
      const now = new Date();
      const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      
      if (selectedMonth === 'current') {
        filtered = filtered.filter(t => {
          const date = new Date(t.date);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          return monthKey === currentMonth;
        });
      } else if (selectedMonth === 'last-month') {
        const lastMonth = new Date();
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        const lastMonthKey = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`;
        filtered = filtered.filter(t => {
          const date = new Date(t.date);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          return monthKey === lastMonthKey;
        });
      } else if (selectedMonth === 'last-3') {
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        filtered = filtered.filter(t => new Date(t.date) >= threeMonthsAgo);
      } else if (selectedMonth === 'last-6') {
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        filtered = filtered.filter(t => new Date(t.date) >= sixMonthsAgo);
      } else {
        // Specific month selected
        filtered = filtered.filter(t => {
          const date = new Date(t.date);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          return monthKey === selectedMonth;
        });
      }
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(t => t.category === selectedCategory);
    }

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter(t => t.type === selectedType);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        t.description.toLowerCase().includes(query) ||
        t.category.toLowerCase().includes(query)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.date) - new Date(a.date);
        case 'date-asc':
          return new Date(a.date) - new Date(b.date);
        case 'amount-desc':
          return b.amount - a.amount;
        case 'amount-asc':
          return a.amount - b.amount;
        default:
          return 0;
      }
    });

    return filtered;
  }, [transactions, selectedMonth, selectedCategory, selectedType, searchQuery, sortBy]);

  return (
    <div className="min-h-screen p-4 lg:p-8 dark:bg-gray-900" data-testid="transactions-page">
      <div className="max-w-7xl mx-auto">
        <Navigation user={user} onLogout={onLogout} currentPage="/transactions" />

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2" data-testid="transactions-title">
              Transactions
            </h1>
            <p className="text-gray-600 dark:text-gray-400">Track your income and expenses</p>
          </div>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={handleAddNew}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-full flex-shrink-0"
                data-testid="add-transaction-button"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Transaction
              </Button>
            </DialogTrigger>
            <DialogContent className="dark:bg-gray-800" data-testid="transaction-dialog">
              <DialogHeader>
                <DialogTitle className="dark:text-gray-100">{editMode ? 'Edit Transaction' : 'Add New Transaction'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="dark:text-gray-300">Type</Label>
                  <Select 
                    value={formData.type} 
                    onValueChange={(value) => {
                      // Clear category when type changes since categories are different for income/expense
                      setFormData({ ...formData, type: value, category: '' });
                    }}
                  >
                    <SelectTrigger data-testid="type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent container={document.body} className="dark:bg-gray-800">
                      <SelectItem value="income">Income (Money received)</SelectItem>
                      <SelectItem value="expense">Expense (Money spent)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="dark:text-gray-300">Category</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger data-testid="category-select" className="dark:bg-gray-700 dark:text-gray-100">
                      <SelectValue placeholder={formData.type === 'income' ? 'Select income category' : 'Select expense category'} />
                    </SelectTrigger>
                    <SelectContent container={document.body} className="dark:bg-gray-800 max-h-[300px]">
                      {(formData.type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map(category => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {formData.type === 'income' ? 'Source of income' : 'What you spent on'}
                  </p>
                </div>

                <div>
                  <Label className="dark:text-gray-300">Amount</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="Enter amount (e.g., 50000)"
                    required
                    data-testid="amount-input"
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <Label className="dark:text-gray-300">Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={formData.type === 'income' ? 'e.g., October salary, Project payment' : 'e.g., Monthly house rent, Grocery shopping at Big Bazaar'}
                    required
                    data-testid="description-input"
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Brief details about this transaction</p>
                </div>

                <div>
                  <Label className="dark:text-gray-300">Date</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    data-testid="date-input"
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">When did this transaction occur? (You can add past or future dates)</p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-teal-600 hover:bg-teal-700"
                  data-testid="submit-transaction"
                >
                  {loading ? (editMode ? 'Updating...' : 'Adding...') : (editMode ? 'Update Transaction' : 'Add Transaction')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filter Section */}
        <div className="mb-6 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by description or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
            />
          </div>

          {/* Filter Chips */}
          <div className="flex flex-wrap gap-3">
            {/* Month Filter */}
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[200px] dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Select Month" />
              </SelectTrigger>
              <SelectContent className="dark:bg-gray-800">
                <SelectItem value="current">This Month</SelectItem>
                <SelectItem value="last-month">Last Month</SelectItem>
                <SelectItem value="last-3">Last 3 Months</SelectItem>
                <SelectItem value="last-6">Last 6 Months</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
                {availableMonths.length > 0 && <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Specific Months</div>}
                {availableMonths.map(month => (
                  <SelectItem key={month.key} value={month.key}>
                    {month.label} ({month.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Category Filter */}
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[180px] dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent className="dark:bg-gray-800">
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Type Filter */}
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="w-[150px] dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent className="dark:bg-gray-800">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="income">Income</SelectItem>
                <SelectItem value="expense">Expense</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort Filter */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px] dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent className="dark:bg-gray-800">
                <SelectItem value="date-desc">Date (Newest First)</SelectItem>
                <SelectItem value="date-asc">Date (Oldest First)</SelectItem>
                <SelectItem value="amount-desc">Amount (High to Low)</SelectItem>
                <SelectItem value="amount-asc">Amount (Low to High)</SelectItem>
              </SelectContent>
            </Select>

            {/* Clear Filters Button */}
            {(searchQuery || selectedMonth !== 'current' || selectedCategory !== 'all' || selectedType !== 'all' || sortBy !== 'date-desc') && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedMonth('current');
                  setSelectedCategory('all');
                  setSelectedType('all');
                  setSortBy('date-desc');
                }}
                className="dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700"
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Results Count */}
          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
            <span>
              Showing <span className="font-semibold text-gray-800 dark:text-gray-200">{filteredTransactions.length}</span> of <span className="font-semibold text-gray-800 dark:text-gray-200">{transactions.length}</span> transactions
            </span>
          </div>
        </div>

        <Card className="glass p-6 dark:bg-gray-800" data-testid="transactions-list">
          <div className="space-y-4">
            {filteredTransactions.length > 0 ? (
              filteredTransactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className="flex items-start justify-between p-4 bg-white dark:bg-gray-700 rounded-xl hover:shadow-md transition-shadow gap-4"
                  data-testid={`transaction-item-${transaction.id}`}
                >
                  <div className="flex items-start space-x-4 flex-1 min-w-0">
                    <div className={`w-12 h-12 rounded-full flex-shrink-0 ${transaction.type === 'income' ? 'bg-emerald-100 dark:bg-emerald-900' : 'bg-rose-100 dark:bg-rose-900'} flex items-center justify-center`}>
                      {transaction.type === 'income' ? (
                        <ArrowUpCircle className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                      ) : (
                        <ArrowDownCircle className="w-6 h-6 text-rose-600 dark:text-rose-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-800 dark:text-gray-100 break-words">{transaction.description}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 break-words">{transaction.category} • {new Date(transaction.date).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-end sm:items-center gap-3 flex-shrink-0">
                    <p className={`text-xl font-bold whitespace-nowrap ${transaction.type === 'income' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                      {transaction.type === 'income' ? '+' : '-'}{formatCurrency(transaction.amount, user?.currency)}
                    </p>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(transaction)}
                        className="h-9 w-9 p-0 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950"
                        data-testid={`edit-transaction-${transaction.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(transaction.id)}
                        className="h-9 w-9 p-0 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
                        data-testid={`delete-transaction-${transaction.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500 dark:text-gray-400 py-12" data-testid="no-transactions">No transactions yet. Add your first transaction!</p>
            )}
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
}